(() => {
  if (window.bayanPdpRuntimeInitialized) return;
  window.bayanPdpRuntimeInitialized = true;

  class BayanPdpController {
    constructor(root) {
      this.root = root;
      this.section = root.closest('.cc-main-product');
      this.formId = root.dataset.bayanProductFormId;
      this.type = root.dataset.bayanPdpType;
      this.usesKaching = root.dataset.bayanUsesKaching === 'true';
      this.productAvailable = root.dataset.bayanProductAvailable === 'true';
      this.expectedRequiredProperties = Number(root.dataset.bayanRequiredPropertyCount || 0);
      this.currentVariantAvailable = root.dataset.bayanCurrentVariantAvailable === 'true';
      this.formVisible = false;
      this.footerVisible = false;
      this.refreshQueued = false;
      this.destroyed = false;

      this.handleInvalid = this.handleInvalid.bind(this);
      this.handleFieldChange = this.handleFieldChange.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleStickyClick = this.handleStickyClick.bind(this);
      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleKachingSelection = this.handleKachingSelection.bind(this);

      this.cacheElements();
      this.bindEvents();
      this.refresh();
    }

    cacheElements() {
      this.form = this.section?.querySelector(`#${CSS.escape(this.formId)}`) || null;
      this.primaryButton = this.form?.querySelector('[name="add"]');
      this.sticky = this.section?.querySelector('[data-bayan-sticky-atc]');
      this.stickyButton = this.sticky?.querySelector('[data-bayan-sticky-submit]');
      this.personalizationLink = this.sticky?.querySelector('[data-bayan-personalization-link]');
      this.kachingLink = this.sticky?.querySelector('[data-bayan-kaching-link]');
    }

    bindEvents() {
      this.section?.addEventListener('invalid', this.handleInvalid, true);
      this.section?.addEventListener('input', this.handleFieldChange);
      this.section?.addEventListener('change', this.handleFieldChange);
      this.section?.addEventListener('click', this.handleStickyClick);
      document.addEventListener('on:variant:change', this.handleVariantChange);
      window.addEventListener('on:breakpoint-change', this.handleBreakpointChange);

      this.contentObserver = new MutationObserver(() => this.queueRefresh());
      if (this.section) {
        this.contentObserver.observe(this.section, {
          childList: true,
          subtree: true
        });
      }

      this.setupVisibilityObservers();
      if (this.usesKaching) this.bindKachingEvents();
    }

    setupVisibilityObservers() {
      if (!this.sticky || !('IntersectionObserver' in window)) return;

      this.formObserver?.disconnect();
      this.formObserver = null;
      this.footerObserver?.disconnect();
      this.footerObserver = null;
      const purchaseTargets = this.getPurchaseTargets();
      this.observedPurchaseTargets = purchaseTargets;
      this.purchaseVisibility = new Map(purchaseTargets.map((target) => [target, false]));
      this.formObserver = new IntersectionObserver((entries, observer) => {
        if (this.destroyed || observer !== this.formObserver) return;
        entries.forEach((entry) => {
          if (this.purchaseVisibility.has(entry.target)) {
            this.purchaseVisibility.set(entry.target, entry.isIntersecting);
          }
        });
        this.formVisible = Array.from(this.purchaseVisibility.values()).some(Boolean);
        this.updateStickyVisibility();
      }, { threshold: 0.08 });
      purchaseTargets.forEach((target) => this.formObserver.observe(target));

      const footer = document.querySelector('footer, .cc-footer');
      if (footer) {
        this.footerObserver = new IntersectionObserver((entries, observer) => {
          if (this.destroyed || observer !== this.footerObserver) return;
          this.footerVisible = entries.some((entry) => entry.isIntersecting);
          this.updateStickyVisibility();
        }, { threshold: 0.02 });
        this.footerObserver.observe(footer);
      } else {
        this.footerVisible = false;
      }
    }

    getPurchaseTargets() {
      const targets = [
        this.form,
        this.section?.querySelector('#bayan-personalization'),
        this.section?.querySelector('kaching-bundles-block')
      ].filter(Boolean);
      return targets.filter((target, index) => targets.indexOf(target) === index);
    }

    purchaseTargetsChanged(targets) {
      const currentTargets = this.observedPurchaseTargets || [];
      return currentTargets.length !== targets.length
        || targets.some((target, index) => target !== currentTargets[index]);
    }

    queueRefresh() {
      if (this.refreshQueued || this.destroyed) return;
      this.refreshQueued = true;
      window.requestAnimationFrame(() => {
        if (this.destroyed) return;
        this.refreshQueued = false;
        this.refresh();
      });
    }

    refresh() {
      if (this.destroyed) return;
      this.cacheElements();
      if (this.purchaseTargetsChanged(this.getPurchaseTargets())) this.setupVisibilityObservers();
      if (this.type === 'personalized') this.enhancePersonalization();
      if (this.usesKaching) this.bindKachingEvents();
      this.syncStickyState();
      this.updateStickyVisibility();
    }

    getRequiredProperties() {
      if (!this.formId || !this.section) return [];
      return Array.from(this.section.querySelectorAll(`[form="${CSS.escape(this.formId)}"][required][name^="properties["]`));
    }

    enhancePersonalization() {
      const fields = this.getRequiredProperties();
      const ready = fields.length >= this.expectedRequiredProperties && this.expectedRequiredProperties > 0;

      if (!ready && this.primaryButton && this.productAvailable) {
        this.primaryButton.dataset.bayanWaitingForOptions = 'true';
        this.primaryButton.disabled = true;
        this.primaryButton.setAttribute('aria-disabled', 'true');
      } else if (ready && this.primaryButton?.dataset.bayanWaitingForOptions === 'true') {
        delete this.primaryButton.dataset.bayanWaitingForOptions;
        this.primaryButton.disabled = !this.currentVariantAvailable;
        if (this.currentVariantAvailable) {
          this.primaryButton.removeAttribute('aria-disabled');
        }
      }

      if (!fields.length) return;
      const holder = fields[0].closest('.product-info__block') || fields[0].parentElement;
      if (holder && !this.section.querySelector('#bayan-personalization')) {
        holder.id = 'bayan-personalization';
      }

      if (holder && !holder.querySelector('.bayan-pdp__required-note')) {
        const note = document.createElement('p');
        note.className = 'bayan-pdp__required-note';
        note.textContent = 'Alle sechs Angaben sind Pflichtfelder. Bitte prüfe sie vor dem Kauf vollständig.';
        holder.prepend(note);
      }

      fields.forEach((field) => {
        field.setAttribute('aria-required', 'true');
        Array.from(field.labels || []).forEach((label) => label.classList.add('bayan-pdp__required-label'));
      });
    }

    handleInvalid(event) {
      const field = event.target;
      if (this.type !== 'personalized' || !this.isRequiredProperty(field)) return;
      this.showFieldError(field);
      this.syncStickyState();
    }

    handleFieldChange(event) {
      const field = event.target;
      if (this.type === 'personalized' && this.isRequiredProperty(field)) {
        if (field.checkValidity()) this.clearFieldError(field);
        this.syncStickyState();
      }
    }

    handleVariantChange(event) {
      const eventForm = event.detail?.form;
      if (eventForm && eventForm.id !== this.formId) return;
      this.currentVariantAvailable = Boolean(event.detail?.variant?.available);
      this.queueRefresh();
    }

    handleBreakpointChange() {
      this.updateStickyVisibility();
    }

    handleStickyClick(event) {
      const stickyButton = event.target.closest('[data-bayan-sticky-submit]');
      if (stickyButton && this.section?.contains(stickyButton)) {
        event.preventDefault();
        this.cacheElements();
        if (this.primaryButton && !this.primaryButton.disabled && this.primaryButton.getAttribute('aria-disabled') !== 'true') {
          this.primaryButton.click();
        }
        return;
      }

      const personalizationLink = event.target.closest('[data-bayan-personalization-link]');
      if (personalizationLink && this.section?.contains(personalizationLink)) {
        const firstInvalid = this.getRequiredProperties().find((field) => !field.checkValidity());
        if (firstInvalid) {
          event.preventDefault();
          firstInvalid.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
          firstInvalid.focus({ preventScroll: true });
          this.showFieldError(firstInvalid);
        }
        return;
      }

      const kachingLink = event.target.closest('[data-bayan-kaching-link]');
      if (kachingLink && this.section?.contains(kachingLink)) {
        const block = this.section.querySelector('kaching-bundles-block');
        if (block) {
          event.preventDefault();
          block.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
        }
      }
    }

    isRequiredProperty(field) {
      return field instanceof HTMLElement && field.matches(`[form="${CSS.escape(this.formId)}"][required][name^="properties["]`);
    }

    showFieldError(field) {
      const errorId = `${field.id || 'bayan-required-field'}-error`;
      let error = this.section?.querySelector(`#${CSS.escape(errorId)}`);
      if (!error) {
        error = document.createElement('p');
        error.id = errorId;
        error.className = 'bayan-pdp__field-error';
        error.textContent = 'Bitte fülle dieses Pflichtfeld aus.';
        field.insertAdjacentElement('afterend', error);
      }
      const describedBy = new Set((field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      describedBy.add(errorId);
      field.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
      field.setAttribute('aria-invalid', 'true');
    }

    clearFieldError(field) {
      const errorId = `${field.id || 'bayan-required-field'}-error`;
      this.section?.querySelector(`#${CSS.escape(errorId)}`)?.remove();
      const describedBy = (field.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && id !== errorId);
      if (describedBy.length) {
        field.setAttribute('aria-describedby', describedBy.join(' '));
      } else {
        field.removeAttribute('aria-describedby');
      }
      field.removeAttribute('aria-invalid');
    }

    syncStickyState() {
      if (!this.sticky) return;
      this.cacheElements();

      if (this.type === 'personalized') {
        const fields = this.getRequiredProperties();
        const ready = fields.length >= this.expectedRequiredProperties && this.expectedRequiredProperties > 0;
        const valid = ready && fields.every((field) => field.checkValidity());
        const canSubmit = valid && this.primaryButton && !this.primaryButton.disabled && this.primaryButton.getAttribute('aria-disabled') !== 'true';

        if (this.personalizationLink) this.personalizationLink.hidden = canSubmit;
        if (this.stickyButton) {
          this.stickyButton.hidden = !canSubmit;
          this.stickyButton.disabled = !canSubmit;
        }
        return;
      }

      if (this.stickyButton && this.primaryButton) {
        const disabled = this.primaryButton.disabled || this.primaryButton.getAttribute('aria-disabled') === 'true';
        this.stickyButton.disabled = disabled;
        const label = this.primaryButton.textContent.trim();
        if (label && this.stickyButton.textContent.trim() !== label) {
          this.stickyButton.textContent = label;
        }
      }
    }

    updateStickyVisibility() {
      if (!this.sticky || window.matchMedia('(min-width: 769px)').matches) return;
      const shouldShow = !this.formVisible && !this.footerVisible;
      this.sticky.hidden = !shouldShow;
      this.sticky.classList.toggle('is-active', shouldShow);
    }

    async bindKachingEvents() {
      if (!window.customElements || this.destroyed) return;
      try {
        await customElements.whenDefined('kaching-bundles-block');
      } catch (error) {
        return;
      }
      if (this.destroyed) return;

      const block = this.section?.querySelector('kaching-bundles-block');
      const status = this.root.querySelector('[data-bayan-kaching-status]');
      if (!block || !status || block.dataset.bayanEventsBound === 'true') return;
      block.dataset.bayanEventsBound = 'true';
      this.kachingBlock = block;

      block.addEventListener('deal-bar-selected', this.handleKachingSelection);
      block.addEventListener('variant-selected', this.handleKachingSelection);
      block.addEventListener('variants-changed', this.handleKachingSelection);
    }

    handleKachingSelection() {
      const status = this.root.querySelector('[data-bayan-kaching-status]');
      if (status) {
        status.hidden = false;
        status.textContent = 'Auswahl aktualisiert.';
      }
    }

    destroy() {
      this.destroyed = true;
      this.section?.removeEventListener('invalid', this.handleInvalid, true);
      this.section?.removeEventListener('input', this.handleFieldChange);
      this.section?.removeEventListener('change', this.handleFieldChange);
      this.section?.removeEventListener('click', this.handleStickyClick);
      document.removeEventListener('on:variant:change', this.handleVariantChange);
      window.removeEventListener('on:breakpoint-change', this.handleBreakpointChange);
      this.contentObserver?.disconnect();
      this.formObserver?.disconnect();
      this.footerObserver?.disconnect();
      if (this.kachingBlock) {
        this.kachingBlock.removeEventListener('deal-bar-selected', this.handleKachingSelection);
        this.kachingBlock.removeEventListener('variant-selected', this.handleKachingSelection);
        this.kachingBlock.removeEventListener('variants-changed', this.handleKachingSelection);
        delete this.kachingBlock.dataset.bayanEventsBound;
      }
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  const initialize = () => {
    document.querySelectorAll('[data-bayan-pdp]').forEach((root) => {
      if (root.dataset.bayanInitialized === 'true') return;
      root.dataset.bayanInitialized = 'true';
      root.bayanPdpController = new BayanPdpController(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-bayan-pdp]').forEach((root) => {
      root.bayanPdpController?.destroy();
      delete root.bayanPdpController;
      delete root.dataset.bayanInitialized;
    });
  });
})();
